// src/pages/Reservations.js — UI Pro Moderne v2
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box, Paper, Stack, Typography, Button, TextField,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Alert, Snackbar, Chip, Tooltip, Dialog,
  DialogTitle, DialogContent, List, ListItem, ListItemText,
  Link as MLink, DialogActions, Select, MenuItem, FormControl,
  InputLabel, Divider, CircularProgress, LinearProgress,
} from "@mui/material";
import EditIcon               from "@mui/icons-material/Edit";
import DeleteIcon             from "@mui/icons-material/Delete";
import RefreshIcon            from "@mui/icons-material/Refresh";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import PlayCircleOutlineIcon  from "@mui/icons-material/PlayCircleOutline";
import TerminalIcon           from "@mui/icons-material/Terminal";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import AddCircleOutlineIcon   from "@mui/icons-material/AddCircleOutline";
import FilterListIcon         from "@mui/icons-material/FilterList";
import OpenInNewIcon          from "@mui/icons-material/OpenInNew";
import AccessTimeIcon         from "@mui/icons-material/AccessTime";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import api, { getUserMessage } from "../api/axios";
import { ReservationsAPI } from "../api/reservationsApi";
import { toUtcIso, fmtLocal, fmtLocalInput } from "../utils/datetime";
import ContextualOnboarding from "../components/ContextualOnboarding";
import { useAuth }            from "../auth/useAuth";
import { useTranslation }     from "react-i18next";

const PAGE_SIZE_DEFAULT = 10;

function safeLower(s) { return (s ?? "").toString().toLowerCase(); }

function uniqBy(arr, keyFn) {
  const seen = new Set(); const out = [];
  for (const x of arr || []) { const k = keyFn(x); if (seen.has(k)) continue; seen.add(k); out.push(x); }
  return out;
}

// ── Statut visuel ─────────────────────────────────────────────────────────────
const STATUS_META = {
  running:   { color: "#22c55e", bg: "#f0fdf4", label: "Running" },
  scheduled: { color: "#6366f1", bg: "#eef2ff", label: "Scheduled" },
  completed: { color: "#64748b", bg: "#f8fafc", label: "Done" },
  cancelled: { color: "#f59e0b", bg: "#fffbeb", label: "Cancelled" },
  failed:    { color: "#ef4444", bg: "#fef2f2", label: "Failed" },
};

function StatusBadge({ status, projectId, deployStatus }) {
  const { t } = useTranslation();
  const isRunning = safeLower(status) === "running";
  const s = isRunning ? "running" : safeLower(status);
  const m = STATUS_META[s] || { color: "#64748b", bg: "#f8fafc", label: s };
  const deployKey = {
    preparing: "reservations.deploy_preparing",
    deploying: "reservations.deploy_deploying",
    stopping:  "reservations.deploy_stopping",
  }[safeLower(deployStatus || "")];
  // UX-STOP-02 : pendant un arret reel, le statut reste "running" (seul
  // stop_reservation_internal le fait passer a "completed", a la toute fin)
  // -- l'indice visuel doit donc aussi s'afficher hors du cas "scheduled",
  // et survivre a un F5 puisqu'il vient de deployStatus (DB), pas de l'etat
  // React en memoire (busyById, perdu au remount).
  const showDeployHint = s === "scheduled" || safeLower(deployStatus || "") === "stopping";
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Box sx={{
        display: "inline-flex", alignItems: "center", gap: 0.5,
        px: 1.5, py: 0.4, borderRadius: 2,
        bgcolor: m.bg, color: m.color,
        fontSize: 11, fontWeight: 700, letterSpacing: .5,
        whiteSpace: "nowrap",
      }}>
        {isRunning && (
          <Box sx={{
            width: 6, height: 6, borderRadius: "50%", bgcolor: m.color,
            animation: "pls 1.5s ease-in-out infinite",
            "@keyframes pls": { "0%,100%": { opacity: 1 }, "50%": { opacity: .3 } },
          }} />
        )}
        {m.label}
      </Box>
      {showDeployHint && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {deployKey ? t(deployKey) : t("reservations.deploy_waiting")}
        </Typography>
      )}
    </Stack>
  );
}

// ── Timer live ────────────────────────────────────────────────────────────────
function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  return now;
}

function TimeRemaining({ endTime }) {
  const { t } = useTranslation();
  const now = useNow();
  const ms = new Date(endTime).getTime() - now;
  if (ms <= 0) return <Typography variant="caption" color="error.main" fontWeight={700}>{t('reservations.expired')}</Typography>;
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const display = h > 0 ? `${h}h ${String(m % 60).padStart(2,"0")}m` : `${String(m).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  const urgent = ms < 15 * 60 * 1000;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <AccessTimeIcon sx={{ fontSize: 12, color: urgent ? "#ef4444" : "#22c55e" }} />
      <Typography variant="caption" fontWeight={700} sx={{ color: urgent ? "#ef4444" : "#22c55e" }}>
        {display}
      </Typography>
    </Stack>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Reservations() {
  const { t } = useTranslation();
  const location = useLocation();
  const [wallets, setWallets]   = useState([]);
  const [labs,    setLabs]      = useState([]);
  const [packId,  setPackId]    = useState("");
  const [labId,   setLabId]     = useState("");
  const [startAt, setStartAt]   = useState(dayjs().add(10, "minute"));
  const [endAt,   setEndAt]     = useState(dayjs().add(1, "hour"));
  const [list,    setList]      = useState([]);
  const [total,   setTotal]     = useState(0);
  const [page,    setPage]      = useState(0);
  const [pageSize,setPageSize]  = useState(PAGE_SIZE_DEFAULT);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading]   = useState(false);
  const [msg,     setMsg]       = useState(null);
  const [error,   setError]     = useState("");
  const [busyById, setBusyById] = useState({});
  const stoppingIdsRef = useRef(new Set());
  const setBusy = (id, patch) => setBusyById(m => ({ ...m, [id]: { ...(m[id]||{}), ...patch } }));
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, text: "" });
  const [confirmStop,   setConfirmStop]   = useState({ open: false, id: null });
  const [edit, setEdit]         = useState({ open: false, id: null, start: null, end: null });
  const [accessData, setAccessData] = useState(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [creditPreview, setCreditPreview] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const newReservationButtonRef = useRef(null);
  const statusColumnHeaderRef = useRef(null);
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Local";
  const { user } = useAuth();
  const isAdmin = safeLower(user?.role) === "admin";
  const navigate = useNavigate();
  const invalidRange = !startAt || !endAt || dayjs(endAt).valueOf() <= dayjs(startAt).valueOf();
  const submitDisabled = loading || !packId || !labId || invalidRange;
  const durationMinutes = (startAt && endAt) ? dayjs(endAt).diff(dayjs(startAt), "minute") : null;
  const formatDuration = (mins) => {
    if (mins == null || mins <= 0) return null;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${m} min`;
  };

  const refresh = useCallback(async (silent = false) => {
    try {
      setLoading(true);
      const qs = `/reservations?limit=${pageSize}&offset=${page * pageSize}` +
        (statusFilter ? `&status_filter=${encodeURIComponent(statusFilter)}` : "");
      const { data } = await api.get(qs);
      const items = Array.isArray(data) ? data : data?.items ?? [];
      setList(items);
      setTotal(typeof data?.total === "number" ? data.total : Array.isArray(data) ? data.length : items.length);
      setError("");
    } catch (e) {
      if (silent) setTimeout(() => refresh(false), 1500);
      else setError(getUserMessage(e, t("errors.load_reservations", { defaultValue: "Failed to load reservations" })));
    } finally { setLoading(false); }
  }, [page, pageSize, statusFilter, t]);

  // Lire les params d'erreur depuis l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errMsg = params.get("message");
    if (errMsg) setError(decodeURIComponent(errMsg));
  }, [location.search]);

  const loadEligible = useCallback(async (selectedPackId, silent = false) => {
    try {
      if (!silent) setError("");
      const params = {};
      if (selectedPackId) params.pack_id = Number(selectedPackId);
      const { data } = await api.get("/labs/eligible", { params });
      const ws    = Array.isArray(data?.wallets) ? data.wallets : [];
      const items = Array.isArray(data?.items)   ? data.items   : [];
      setWallets(ws); setLabs(items); setError("");
      // Auto-sélection : prendre le premier wallet avec des crédits disponibles
      const wsWithCredits = ws.filter(w => w.remaining_minutes > 0 && w.pack_id);
      if (!selectedPackId && !packId && wsWithCredits.length >= 1) {
        const autoPackId = String(wsWithCredits[0].pack_id);
        setPackId(autoPackId);
        const numericId = !autoPackId.startsWith("legacy_") ? Number(autoPackId) : undefined;
        if (numericId) {
          const { data: data2 } = await api.get("/labs/eligible", { params: { pack_id: numericId } });
          setLabs(Array.isArray(data2?.items) ? data2.items : []);
        }
      }
      if (items.length && !labId) setLabId(String(items[0].id));
      else if (labId && !items.some(l => String(l.id) === String(labId)))
        setLabId(items.length ? String(items[0].id) : "");
    } catch (e) {
      if (silent) setTimeout(() => loadEligible(selectedPackId, false), 1500);
      else { setError(getUserMessage(e, t("errors.load_labs", { defaultValue: "Failed to load labs" }))); setWallets([]); setLabs([]); setLabId(""); }
    }
  }, [labId, packId, t]);

  useEffect(() => {
    refresh(true);
    const timer = setTimeout(() => loadEligible(undefined, true), 200);
    // Polling toutes les 30s pour mettre à jour les statuts (running → completed après expiration)
    const poll = setInterval(() => refresh(true), 30_000);
    return () => { clearTimeout(timer); clearInterval(poll); };
  }, []);

  const _refreshOnChange = useRef(false);
  useEffect(() => {
    if (!_refreshOnChange.current) { _refreshOnChange.current = true; return; }
    refresh(false);
  }, [refresh]);

  const selectedWallet = useMemo(() => wallets.find(w => String(w.pack_id) === String(packId)) || null, [wallets, packId]);
  const packOptions    = useMemo(() => uniqBy(wallets, w => `${w.pack_id}:${w.pack_code||""}`).filter(w => w.pack_id && w.remaining_minutes > 0)
    .map(w => ({ pack_id: w.pack_id, label: `${w.pack_code || `Pack#${w.pack_id}`} — ${w.remaining_minutes ?? 0}/${w.total_minutes ?? "?"} min` })), [wallets]);
  const labOptions     = useMemo(() => (labs||[]).map(l => ({ ...l, _packsLabel: (l.allowed_by||[]).map(p => p.pack_code||`Pack#${p.pack_id}`).filter(Boolean).join(", ") })), [labs]);

  const submit = async () => {
    try {
      setError(""); setLoading(true);
      if (!packId) return setError("Pack requis");
      if (!labId)  return setError("Lab requis");
      if (invalidRange) return setError(t("reservations.validation_end_after_start", { defaultValue: "End must be after start" }));
      // pack_id peut être "legacy_X" pour les wallets sans pack → envoyer null
      const numericPackId = packId && !String(packId).startsWith("legacy_") ? Number(packId) : null;
      const payload = { lab_id: Number(labId), pack_id: numericPackId, start_time: toUtcIso(startAt), end_time: toUtcIso(endAt) };
      try {
        const { data: check } = await api.post("/reservations/pro-check", payload);
        if (check?.allowed === false) {
          const codes = Array.isArray(check.error_codes) ? check.error_codes : [];
          const errs  = Array.isArray(check.errors) ? check.errors : [];
          setError((codes.length ? codes.map(c => t(`errors.${c}`, { defaultValue: c })) : errs.length ? errs : ["Unknown"]).join(" | "));
          setCreditPreview(null); return;
        }
        if (check?.credits?.status === "OK") setCreditPreview({ packCode: check.credits.lab_pack_code || selectedWallet?.pack_code || "N/A", before: check.credits.remaining_before ?? 0, after: check.credits.remaining_after ?? 0 });
        else setCreditPreview(null);
      } catch (e) { setError(getUserMessage(e, "Unknown error")); setCreditPreview(null); return; }
      await api.post("/reservations", payload);
      setStartAt(dayjs().add(10, "minute")); setEndAt(dayjs().add(1, "hour"));
      setMsg({ text: t("reservations.snack.created", { defaultValue: "Reservation created" }), type: "success" });
      setFormOpen(false); setPage(0); await refresh();
    } catch (e) { setError(getUserMessage(e, "Unknown error")); } finally { setLoading(false); }
  };

  const cancelReservation = async () => {
    try {
      if (!confirmDelete.id) return;
      if (confirmDelete.text.trim().toUpperCase() !== "CONFIRMER") { setError("Tape exactement CONFIRMER"); return; }
      await api.delete(`/reservations/${confirmDelete.id}`);
      setConfirmDelete({ open: false, id: null, text: "" });
      setMsg({ text: t("reservations.snack.deleted", { defaultValue: "Deleted" }), type: "success" }); await refresh();
    } catch (e) { setError(getUserMessage(e, "Unknown error")); }
  };

  const saveEdit = async () => {
    try {
      if (!edit.id || !edit.start || !edit.end) return;
      if (dayjs(edit.end).valueOf() <= dayjs(edit.start).valueOf()) return setError(t('reservations.validation_end_after_start'));
      await api.put(`/reservations/${edit.id}`, { start_time: toUtcIso(edit.start), end_time: toUtcIso(edit.end) });
      setMsg({ text: t("reservations.snack.updated", { defaultValue: "Updated" }), type: "success" });
      setEdit({ open: false, id: null, start: null, end: null }); await refresh();
    } catch (e) { setError(getUserMessage(e, "Unknown error")); }
  };

  const onAccess  = (id) => navigate(`/lab/${id}`);
  const closeAccess = () => { setAccessOpen(false); setAccessData(null); };
  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); setMsg({ text: t('common.copied_short'), type: "success" }); }
    catch { setMsg({ text: t('common.copy_failed'), type: "error" }); }
  };

  const startNow  = async (id) => { try { setBusy(id, { start: true }); await api.post(`/gns3/reservations/${id}/start`); setMsg({ text: t('reservations.snack_started'), type: "success" }); await refresh(); } catch (e) { setError(getUserMessage(e, "Erreur")); } finally { setBusy(id, { start: false }); } };
  // Arret synchrone (ReservationsAPI.stop -> POST /api/v1/reservations/{id}/stop) :
  // la reservation est deja dans son etat final (status, end_time recalcule)
  // quand cette promesse se resout, contrairement a l'ancien appel direct vers
  // l'endpoint asynchrone /gns3/reservations/{id}/stop (job APScheduler execute
  // apres la reponse HTTP), qui laissait le timer tourner jusqu'au prochain
  // polling de secours.
  //
  // stoppingIdsRef (et non busyById, qui est de l'etat React asynchrone) :
  // deux clics synchrones rapprochees sur le bouton "Arreter le lab" du
  // Dialog peuvent s'executer avant que busyById ne soit effectivement
  // commit/relu -- un Set mutable lu/ecrit de maniere synchrone au tout
  // debut de la fonction garantit qu'un second appel concurrent pour le
  // meme id est rejete immediatement, independamment du cycle de rendu.
  const stopNow   = async (id) => {
    if (stoppingIdsRef.current.has(id)) return;
    stoppingIdsRef.current.add(id);
    try {
      setBusy(id, { stop: true });
      await ReservationsAPI.stop(id);
      setMsg({ text: t('reservations.snack_stopped'), type: "success" });
      await refresh();
    } catch (e) {
      setMsg({ text: getUserMessage(e, "Erreur"), type: "error" });
    } finally {
      stoppingIdsRef.current.delete(id);
      setBusy(id, { stop: false });
      setConfirmStop({ open: false, id: null });
    }
  };
  const startNode = async (id) => { try { setBusy(id, { nodesStart: true }); await api.post(`/gns3/reservations/${id}/nodes/start-all`, {}); setMsg({ text: t('reservations.snack_nodes_start'), type: "success" }); await refresh(); } catch (e) { setError(getUserMessage(e, "Erreur")); } finally { setBusy(id, { nodesStart: false }); } };
  const stopNode  = async (id) => { try { setBusy(id, { nodesStop: true }); await api.post(`/gns3/reservations/${id}/nodes/stop-all`, {}); setMsg({ text: t('reservations.snack_nodes_stop'), type: "success" }); await refresh(); } catch (e) { setError(getUserMessage(e, "Erreur")); } finally { setBusy(id, { nodesStop: false }); } };

  const canPrev    = page > 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canNext    = page + 1 < totalPages;

  const STATUS_OPTIONS = useMemo(() => [
    { value: "scheduled", label: t('status.scheduled') },
    { value: "running",   label: t('status.running') },
    { value: "completed", label: t('status.completed') },
    { value: "cancelled", label: t('status.cancelled') },
    { value: "failed",    label: t('status.failed') },
  ], [t]);

  const onPackChange = async (value) => { setPackId(value); setCreditPreview(null); await loadEligible(value ? Number(value) : undefined); };

  const runningCount = list.filter(r => safeLower(r.status) === "running").length;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", my: 4, px: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}
        justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('reservations.title')}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
            <Typography variant="body2" color="text.secondary">
              {total} réservation{total !== 1 ? "s" : ""} · Fuseau : {localTz}
            </Typography>
            {runningCount > 0 && (
              <Chip size="small" label={`${runningCount} actif${runningCount > 1 ? "s" : ""}`}
                sx={{ bgcolor: "#f0fdf4", color: "#22c55e", fontWeight: 700, height: 20, fontSize: 11 }} />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('reservations.status')}</InputLabel>
            <Select label="Statut" value={statusFilter}
              onChange={e => { setPage(0); setStatusFilter(e.target.value); }}
              sx={{ borderRadius: 2 }}>
              {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title="Actualiser">
            <IconButton onClick={refresh} disabled={loading} size="small"
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />}
            ref={newReservationButtonRef}
            onClick={() => setFormOpen(f => !f)}
            sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
            Nouvelle réservation
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* ── Formulaire de création (collapsible) ── */}
      {formOpen && (
        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "#6366f1", borderRadius: 3, p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="subtitle1" fontWeight={700}>{t('reservations.create_title')}</Typography>
              <Typography variant="caption" color="text.secondary">Pro-check automatique avant confirmation</Typography>
            </Stack>
            <Chip size="small" label={`${localTz}`} variant="outlined" sx={{ fontSize: 11 }} />
          </Stack>

          <Stack spacing={2}>
            {/* Pack + solde */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>{t('reservations.pack')}</InputLabel>
                <Select label="Pack" value={packId} onChange={e => onPackChange(e.target.value)}
                  sx={{ borderRadius: 2 }}>
                  <MenuItem value="" disabled>{t('reservations.pack_choose')}</MenuItem>
                  {packOptions.map(p => <MenuItem key={p.pack_id} value={String(p.pack_id)}>{p.label}</MenuItem>)}
                </Select>
              </FormControl>
              {selectedWallet && (
                <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">Solde :</Typography>
                    <Typography variant="body2" fontWeight={700} color="#22c55e">
                      {selectedWallet.remaining_minutes ?? 0} min
                    </Typography>
                    <Typography variant="caption" color="text.disabled">/ {selectedWallet.total_minutes ?? "?"} min</Typography>
                  </Stack>
                  {selectedWallet.total_minutes > 0 && (
                    <LinearProgress variant="determinate"
                      value={Math.min(100, ((selectedWallet.remaining_minutes ?? 0) / selectedWallet.total_minutes) * 100)}
                      sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: "#bbf7d0", "& .MuiLinearProgress-bar": { bgcolor: "#22c55e" } }}
                    />
                  )}
                </Box>
              )}
            </Stack>

            {/* Lab */}
            <FormControl size="small" fullWidth>
              <InputLabel>{t('reservations.lab')}</InputLabel>
              <Select label="Lab" value={labId} onChange={e => setLabId(e.target.value)}
                disabled={!packId} sx={{ borderRadius: 2 }}>
                <MenuItem value="" disabled>{t('reservations.lab_choose')}</MenuItem>
                {labOptions.map(l => (
                  <MenuItem key={l.id} value={String(l.id)}>
                    <Stack>
                      <Typography variant="body2" fontWeight={600}>{l.name || `Lab #${l.id}`}</Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {(l.tags||[]).slice(0,4).map(tg => <Chip key={tg} size="small" variant="outlined" label={tg} sx={{ height: 16, fontSize: 10 }} />)}
                        {l._packsLabel && <Chip size="small" color="success" label={l._packsLabel} sx={{ height: 16, fontSize: 10 }} />}
                      </Stack>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Dates */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField size="small" fullWidth label={t('reservations.date_from')} type="datetime-local"
                value={fmtLocalInput(startAt)} onChange={e => setStartAt(dayjs(e.target.value))}
                InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              <TextField size="small" fullWidth label={t('reservations.date_to')} type="datetime-local"
                value={fmtLocalInput(endAt)} onChange={e => setEndAt(dayjs(e.target.value))}
                InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            </Stack>

            {!invalidRange && durationMinutes != null && formatDuration(durationMinutes) && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">{t('reservations.duration')} :</Typography>
                <Typography variant="body2" fontWeight={700}>{formatDuration(durationMinutes)}</Typography>
              </Stack>
            )}

            {invalidRange && <Alert severity="warning" sx={{ borderRadius: 2 }}>{t('reservations.validation_end_after_start')}</Alert>}
            {creditPreview && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Crédits : {creditPreview.before} → <strong>{creditPreview.after} min</strong> (pack {creditPreview.packCode})
              </Alert>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setFormOpen(false)} sx={{ borderRadius: 2 }}>{t('common.cancel')}</Button>
              <Button variant="contained" onClick={submit} disabled={submitDisabled}
                sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
                {loading ? <CircularProgress size={16} sx={{ color: "white" }} /> : t('reservations.confirm_create')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* ── Tableau ── */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterListIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="subtitle1" fontWeight={600}>
              {statusFilter ? STATUS_OPTIONS.find(o => o.value === statusFilter)?.label : t('reservations.all_title')}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {loading && <CircularProgress size={14} />}
            <Typography variant="caption" color="text.secondary">
              {list.length} / {total}
            </Typography>
          </Stack>
        </Stack>

        {list.length === 0 && !loading ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography color="text.secondary">{t('reservations.none')}</Typography>
            <Button variant="outlined" sx={{ mt: 2, borderRadius: 2 }} startIcon={<AddCircleOutlineIcon />}
              onClick={() => setFormOpen(true)}>
              Créer une réservation
            </Button>
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Lab</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>{t('reservations.date_from')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>{t('reservations.fin_restant')}</TableCell>
                  <TableCell ref={statusColumnHeaderRef} sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Statut</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map(r => {
                  const status = safeLower(r.status || "scheduled");
                  const isRunning = status === "running";
                  const canEdit   = status === "scheduled";
                  const canDelete = status !== "running";
                  const busy = busyById[r.id] || {};
                  return (
                    <TableRow key={r.id} hover sx={{ "&:hover": { bgcolor: "#f8fafc" }, "&:last-child td": { border: 0 }, transition: "background .15s" }}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="body2" fontWeight={600}>
                              {r.lab_name || `Lab #${r.lab_id}`}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">#{r.id}</Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {r.pack_id && <Chip size="small" variant="outlined" label={`Pack #${r.pack_id}`} sx={{ height: 18, fontSize: 10 }} />}
                            {r.consumed_minutes != null && <Chip size="small" variant="outlined" label={`${r.consumed_minutes} min`} sx={{ height: 18, fontSize: 10 }} />}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                        {fmtLocal(r.start_time)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {isRunning && new Date(r.end_time) > new Date()
                          ? <TimeRemaining endTime={r.end_time} />
                          : <Typography variant="caption" color="text.secondary">{fmtLocal(r.end_time)}</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={r.status} projectId={r.project_id} deployStatus={r.deploy_status} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                          {isRunning && r.project_id && (
                            <Button size="small" variant="contained" startIcon={<OpenInNewIcon fontSize="small" />}
                              onClick={() => onAccess(r.id)}
                              sx={{ borderRadius: 2, fontSize: 11, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
                              Workspace
                            </Button>
                          )}
                          {isRunning && r.project_id && (
                            <Tooltip title={t('reservations.stop_action')}>
                              <IconButton size="small" onClick={() => setConfirmStop({ open: true, id: r.id })}
                                disabled={!!busy.stop}
                                sx={{ color: "#f59e0b", "&:hover": { bgcolor: "#fffbeb" } }}>
                                {busy.stop ? <CircularProgress size={14} /> : <StopCircleOutlinedIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            <>
                              <Tooltip title={t('reservations.snack_started')}>
                                <IconButton size="small" onClick={() => startNow(r.id)} disabled={!!busy.start}
                                  sx={{ color: "#22c55e", "&:hover": { bgcolor: "#f0fdf4" } }}>
                                  {busy.start ? <CircularProgress size={14} /> : <PlayCircleOutlineIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              {isRunning && r.project_id && (
                                <>
                                  <Tooltip title={t('workspace.start_all')}>
                                    <IconButton size="small" onClick={() => startNode(r.id)} disabled={!!busy.nodesStart}
                                      sx={{ fontSize: 11, "&:hover": { bgcolor: "#f0fdf4" } }}>
                                      {busy.nodesStart ? <CircularProgress size={14} /> : <TerminalIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('workspace.stop_all')}>
                                    <IconButton size="small" onClick={() => stopNode(r.id)} disabled={!!busy.nodesStop}
                                      sx={{ "&:hover": { bgcolor: "#fef2f2" } }}>
                                      {busy.nodesStop ? <CircularProgress size={14} /> : <StopCircleOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </>
                          )}
                          <Tooltip title={canEdit ? "Modifier" : "Non modifiable"}>
                            <span>
                              <IconButton size="small" disabled={!canEdit} onClick={() => setEdit({ open: true, id: r.id, start: dayjs(r.start_time), end: dayjs(r.end_time) })}
                                sx={{ "&:hover": { bgcolor: "#eef2ff" } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDelete ? "Supprimer" : t('reservations.actif_non_supprimable')}>
                            <span>
                              <IconButton size="small" color="error" disabled={!canDelete}
                                onClick={() => setConfirmDelete({ open: true, id: r.id, text: "" })}
                                sx={{ "&:hover": { bgcolor: "#fef2f2" } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            <Stack direction="row" alignItems="center" justifyContent="space-between"
              sx={{ px: 2, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" variant="outlined" disabled={!canPrev || loading}
                  onClick={() => setPage(p => Math.max(0, p-1))}
                  sx={{ borderRadius: 2, fontSize: 12 }}>← Préc.</Button>
                <Typography variant="caption" color="text.secondary">
                  Page {page+1} / {totalPages}
                </Typography>
                <Button size="small" variant="outlined" disabled={!canNext || loading}
                  onClick={() => setPage(p => p+1)}
                  sx={{ borderRadius: 2, fontSize: 12 }}>Suiv. →</Button>
              </Stack>
              <FormControl size="small">
                <Select value={pageSize} onChange={e => { setPage(0); setPageSize(Number(e.target.value)||PAGE_SIZE_DEFAULT); }}
                  sx={{ borderRadius: 2, fontSize: 12, "& .MuiSelect-select": { py: 0.5 } }}>
                  {[5,10,25,50].map(n => <MenuItem key={n} value={n}>{n} / page</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
          </>
        )}
      </Paper>

      {/* ── Dialogs ── */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null, text: "" })} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('reservations.confirm_cancel_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
            Tapez <strong>{t('reservations.confirmer_word')}</strong> pour annuler la réservation #{confirmDelete.id}.
          </Typography>
          <TextField fullWidth size="small" placeholder="CONFIRMER" value={confirmDelete.text}
            onChange={e => setConfirmDelete(c => ({ ...c, text: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete({ open: false, id: null, text: "" })} sx={{ borderRadius: 2 }}>Retour</Button>
          <Button color="error" variant="contained" onClick={cancelReservation} sx={{ borderRadius: 2 }}>{t('reservations.cancel_action')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmStop.open} onClose={() => setConfirmStop({ open: false, id: null })} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('reservations.stop_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Les crédits consommés seront débités et une notification email sera envoyée.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmStop({ open: false, id: null })} sx={{ borderRadius: 2 }}>Annuler</Button>
          <Button variant="contained" color="warning" onClick={() => confirmStop.id && stopNow(confirmStop.id)}
            disabled={!confirmStop.id || !!busyById[confirmStop.id]?.stop}
            startIcon={confirmStop.id && busyById[confirmStop.id]?.stop ? <CircularProgress size={14} /> : <StopCircleOutlinedIcon />}
            sx={{ borderRadius: 2 }}>
            Arrêter le lab
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!edit.open} onClose={() => setEdit({ open: false, id: null, start: null, end: null })} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Modifier la réservation #{edit.id}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField label={t('reservations.date_from')} type="datetime-local" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={edit.start ? fmtLocalInput(edit.start) : ""} onChange={e => setEdit(s => ({ ...s, start: dayjs(e.target.value) }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <TextField label="Fin" type="datetime-local" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={edit.end ? fmtLocalInput(edit.end) : ""} onChange={e => setEdit(s => ({ ...s, end: dayjs(e.target.value) }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEdit({ open: false, id: null, start: null, end: null })} sx={{ borderRadius: 2 }}>Annuler</Button>
          <Button variant="contained" onClick={saveEdit} sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>Sauvegarder</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={accessOpen} onClose={closeAccess} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('reservations.access_console')}</DialogTitle>
        <DialogContent dividers>
          {!accessData && <Typography>{t('common.loading')}</Typography>}
          {accessData && (
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Projet : <strong>{accessData.project_id}</strong></Typography>
                <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => copyToClipboard(JSON.stringify(accessData, null, 2))} sx={{ borderRadius: 2 }}>
                  Copier JSON
                </Button>
              </Stack>
              <List dense>
                {(accessData.nodes||[]).map(n => (
                  <ListItem key={n.node_id} disableGutters>
                    <ListItemText primary={n.name || n.node_id}
                      secondary={n.console_type && n.console_port ? `${(n.console_type||"").toUpperCase()} : ${n.console_port}` : "Pas de console"} />
                    {n.link && <MLink href={n.link} target="_blank" rel="noreferrer">{t('reservations.ouvrir')}</MLink>}
                  </ListItem>
                ))}
                {(!accessData.nodes || accessData.nodes.length === 0) && (
                  <ListItem><ListItemText primary="Aucun nœud n'expose de console." /></ListItem>
                )}
              </List>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAccess} sx={{ borderRadius: 2 }}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!msg} autoHideDuration={4500} onClose={() => setMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={msg?.type || "info"} onClose={() => setMsg(null)} sx={{ borderRadius: 2 }}>
          {msg?.text}
        </Alert>
      </Snackbar>

      <ContextualOnboarding
        pageKey="reservations"
        steps={[
          {
            title: t("onboarding.reservations.step1_title"),
            text: t("onboarding.reservations.step1_text"),
            targetRef: newReservationButtonRef,
            placement: "bottom",
          },
          {
            title: t("onboarding.reservations.step2_title"),
            text: t("onboarding.reservations.step2_text"),
            targetRef: statusColumnHeaderRef,
            placement: "bottom",
          },
        ]}
      />
    </Box>
  );
}